import type { ReactNode, RefObject } from "react"
import { useEffect, useRef, useState } from "react"

import { motion, type MotionValue, useScroll, useTransform } from "motion/react"

import { cn } from "@/lib/utils"

export type ContainerScrollProps = {
  titleComponent?: ReactNode
  children: ReactNode
  className?: string
}

export function ContainerScroll({ titleComponent, children, className }: ContainerScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
  })
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => {
      window.removeEventListener("resize", checkMobile)
    }
  }, [])

  const scaleDimensions = () => {
    return isMobile ? [0.7, 0.9] : [1.05, 1]
  }

  const rotate = useTransform(scrollYProgress, [0, 1], [20, 0])
  const scale = useTransform(scrollYProgress, [0, 1], scaleDimensions())
  const translate = useTransform(scrollYProgress, [0, 1], [0, -100])

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex h-[60rem] items-center justify-center p-2 md:h-[80rem] md:p-20",
        className
      )}
    >
      <div
        className="relative w-full py-10 md:py-40"
        style={{
          perspective: "1000px",
        }}
      >
        {titleComponent && <Header titleComponent={titleComponent} translate={translate} />}
        <Card rotate={rotate} scale={scale} translate={translate}>
          {children}
        </Card>
      </div>
    </div>
  )
}

type HeaderProps = {
  translate: MotionValue<number>
  titleComponent: ReactNode
}

function Header({ translate, titleComponent }: HeaderProps) {
  return (
    <motion.div className="mx-auto max-w-5xl text-center" style={{ translateY: translate }}>
      {titleComponent}
    </motion.div>
  )
}

type CardProps = {
  rotate: MotionValue<number>
  scale: MotionValue<number>
  translate: MotionValue<number>
  children: ReactNode
}

function Card({ rotate, scale, children }: CardProps) {
  return (
    <motion.div
      className="border-border bg-card mx-auto -mt-12 h-[30rem] w-full max-w-5xl rounded-[30px] border-4 p-2 shadow-2xl md:h-[40rem] md:p-6"
      style={{
        rotateX: rotate,
        scale,
        boxShadow:
          "0 0 #0000004d, 0 9px 20px #0000004a, 0 37px 37px #00000042, 0 84px 50px #00000026, 0 149px 60px #0000000a, 0 233px 65px #00000003",
      }}
    >
      <div className="bg-muted h-full w-full overflow-hidden rounded-lg md:rounded-lg md:p-4">
        {children}
      </div>
    </motion.div>
  )
}

// Simplified variant for inline scroll-tilt effect (used in workflow landing page)
export type ScrollTiltCardProps = {
  children: ReactNode
  className?: string
  containerClassName?: string
  scrollContainerRef?: RefObject<HTMLElement | null>
}

export function ScrollTiltCard({
  children,
  className,
  containerClassName,
  scrollContainerRef,
}: ScrollTiltCardProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isMobile, setIsMobile] = useState(false)
  // Track when the scroll container is ready to force useScroll re-initialization
  const [isContainerReady, setIsContainerReady] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => {
      window.removeEventListener("resize", checkMobile)
    }
  }, [])

  // Wait for the scroll container ref to be available (fixes production build timing)
  useEffect(() => {
    if (scrollContainerRef?.current) {
      setIsContainerReady(true)
    }
  }, [scrollContainerRef])

  // Don't render scroll-tracked content until container is ready
  if (!isContainerReady) {
    return (
      <div className={cn("relative", containerClassName)} style={{ perspective: "1200px" }}>
        <div className={cn("origin-bottom", className)}>{children}</div>
      </div>
    )
  }

  return (
    <ScrollTiltCardInner
      className={className}
      containerClassName={containerClassName}
      containerRef={containerRef}
      isMobile={isMobile}
      scrollContainerRef={scrollContainerRef}
    >
      {children}
    </ScrollTiltCardInner>
  )
}

// Inner component that only mounts when scroll container is ready
type ScrollTiltCardInnerProps = {
  children: ReactNode
  className?: string
  containerClassName?: string
  scrollContainerRef?: RefObject<HTMLElement | null>
  containerRef: RefObject<HTMLDivElement | null>
  isMobile: boolean
}

function ScrollTiltCardInner({
  children,
  className,
  containerClassName,
  scrollContainerRef,
  containerRef,
  isMobile,
}: ScrollTiltCardInnerProps) {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    container: scrollContainerRef,
    // Animation starts when card peeks at bottom of viewport, completes when card is 40% from top
    offset: ["start 0.95", "start 0.4"],
  })

  // Pronounced tilt when first visible, reduced on mobile for usability
  const rotateX = useTransform(scrollYProgress, [0, 1], [isMobile ? 15 : 28, 0])
  const scale = useTransform(scrollYProgress, [0, 1], [isMobile ? 0.88 : 0.92, 1])

  // Shadow that deepens with tilt for 3D depth effect
  const shadowY = useTransform(scrollYProgress, [0, 1], [25, 8])
  const shadowBlur = useTransform(scrollYProgress, [0, 1], [40, 16])
  const shadowOpacity = useTransform(scrollYProgress, [0, 1], [0.25, 0.1])

  return (
    <div
      ref={containerRef}
      className={cn("relative", containerClassName)}
      style={{ perspective: "1200px" }}
    >
      <motion.div
        className={cn("origin-bottom", className)}
        style={{
          rotateX,
          scale,
          boxShadow: useTransform(
            [shadowY, shadowBlur, shadowOpacity],
            ([y, blur, opacity]) => `0 ${y}px ${blur}px rgba(0, 0, 0, ${opacity})`
          ),
        }}
      >
        {children}
      </motion.div>
    </div>
  )
}
